/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { AttachmentListComponent, SharedComponentsModule } from '@well/shared-components';
import {
  TaskCheckpointStatusType,
  TaskCheckpointType,
  TaskCheckpointViewVoModel
} from '@well/delivery-libs/task-management/domain';

@Component({
  selector: 'well-task-checkpoint-tracking-details',
  templateUrl: './task-checkpoint-tracking-details.component.html',
  styleUrls: ['./task-checkpoint-tracking-details.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule, SharedComponentsModule, AttachmentListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskCheckpointTrackingDetailsComponent {
  checkpoint = input.required<TaskCheckpointViewVoModel>();
  type = input.required<TaskCheckpointType>();

  expanded = signal(false);

  private translate = inject(TranslateService);

  toggleExpanded(): void {
    this.expanded.update(v => !v);
  }

  typeTranslationKey = computed(() => {
    switch (this.type()) {
      case TaskCheckpointType.PICKUP:
        return 'dispatcher.tasks.taskStatusChange.pickup';
      case TaskCheckpointType.DELIVERY:
        return 'dispatcher.tasks.taskStatusChange.delivery';
      case TaskCheckpointType.RETURN:
        return 'dispatcher.tasks.taskStatusChange.return';
      default:
        return '';
    }
  });

  private static readonly EXECUTED_BY_TYPE_ICONS: Record<string, string> = {
    DRIVER: 'fa-truck',
    WAREHOUSE: 'fa-warehouse',
    PREORDER_LINK: 'fa-link',
    OFFICE: 'fa-globe',
  };

  executedByTypeIcon = computed(() => {
    const type = this.checkpoint().proofOfHandoverExecutedByType;
    return type ? TaskCheckpointTrackingDetailsComponent.EXECUTED_BY_TYPE_ICONS[type] : null;
  });

  executedByTypeTooltip = computed(() => {
    const type = this.checkpoint().proofOfHandoverExecutedByType;
    return type ? `dispatcher.tasks.proofOfHandover.executedByType.${type}` : '';
  });

  hasProofOfHandover = computed(() => {
    const cp = this.checkpoint();
    return cp.proofOfHandoverDate != null;
  });

  isProofOfHandoverCompleted = computed(() => {
    const status = this.checkpoint().status;
    return status === TaskCheckpointStatusType.completed || status === TaskCheckpointStatusType.completed_delayed;
  });

  isProofOfHandoverRefused = computed(() => {
    return this.checkpoint().status === TaskCheckpointStatusType.refused;
  });

  isProofOfHandoverUnavailable = computed(() => {
    return this.checkpoint().status === TaskCheckpointStatusType.unavailable;
  });

  hasExpandableContent = computed(() => {
    return !!this.proofOfHandoverPersonName() ||
           !!this.checkpoint().proofOfHandoverComment ||
           this.attachments().length > 0 ||
           this.signatures().length > 0;
  });

  proofOfHandoverPersonName = computed(() => {
    const cp = this.checkpoint();
    return cp.proofOfHandoverDriverName ?? cp.proofOfHandoverWarehouseName ?? '';
  });

  location = computed(() => {
    const loc = this.checkpoint().locationDTO;
    if (!loc) {
      return '';
    }
    const parts = [];
    if (loc.city) {
      parts.push(loc.city);
    }
    if (loc.country) {
      parts.push(loc.country);
    }
    return parts.join(', ');
  });

  attachments = computed(() => this.checkpoint().proofOfHandoverAttachments);

  signatures = computed(() => {
    return this.checkpoint().proofOfHandoverSignatures.map(sig => {
      if (sig.responsibleType) {
        const signatureLabel = this.translate.instant('dispatcher.tasks.proofOfHandover.signature');
        const typeLabel = this.translate.instant(`dispatcher.tasks.proofOfHandover.responsibleType.${sig.responsibleType}`);
        return { ...sig, fileName: `${signatureLabel} - ${typeLabel}` };
      }
      return sig;
    });
  });
}
